import { createUseStyles } from "react-jss";
import { AvailabilityPage } from "./components/availability/AvailabilityPage";
import { AcceptInvitePage } from "./components/auth/AcceptInvitePage";
import { ForgotPasswordPage } from "./components/auth/ForgotPasswordPage";
import { LoginPage } from "./components/auth/LoginPage";
import { ResetPasswordPage } from "./components/auth/ResetPasswordPage";
import { AgreementPage } from "./components/agreements/AgreementPage";
import { GuestWaiverPage } from "./components/agreements/GuestWaiverPage";
import { PublicAgreementPage } from "./components/agreements/PublicAgreementPage";
import { AdminBoatsPage } from "./components/boat/AdminBoatsPage";
import { BoatPage } from "./components/boat/BoatPage";
import { BoatCalendarPage } from "./components/calendar/BoatCalendarPage";
import { FindBoatPage } from "./components/find/FindBoatPage";
import { NavBar } from "./components/layout/NavBar";
import { SiteFooter } from "./components/layout/SiteFooter";
import { ReservePage } from "./components/reserve/ReservePage";
import { ReservationsPage } from "./components/reservations/ReservationsPage";
import { LoadError, Loading } from "./components/shared/StatusViews";
import { UsersPage } from "./components/users/UsersPage";
import { useRoute } from "./hooks/useRoute";
import type { Route } from "./routes";
import { DEFAULT_ROUTE, canView, isPublicRoute, routeToHash } from "./routes";
import { useCurrentUser, useSession } from "./session/SessionContext";
import type { VetBoatsTheme } from "./theme";

const useStyles = createUseStyles((theme: VetBoatsTheme) => ({
  "@global": {
    "*, *::before, *::after": { boxSizing: "border-box" },
    body: {
      margin: 0,
      fontFamily: theme.font,
      fontSize: 15,
      color: theme.color.text,
      background: theme.color.background,
      WebkitFontSmoothing: "antialiased",
    },
    "h1, h2, h3": { lineHeight: 1.25 },
    a: { color: theme.color.primary },
  },
  main: {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "24px 16px 48px",
    [theme.narrow]: { padding: "16px 12px 40px" },
  },
  centered: { maxWidth: 480, margin: "80px auto", padding: 16 },
}));

const PageContent = ({ route }: { route: Route }) => {
  switch (route.page) {
    case "find":
      return <FindBoatPage trip={route.trip} />;
    case "boat":
      return <BoatCalendarPage boatId={route.boatId} trip={route.trip} />;
    case "reservations":
      return <ReservationsPage />;
    case "adminBoats":
      return <AdminBoatsPage />;
    case "adminBoat":
      // Remount per boat so the editor never shows a previous boat's state.
      return <BoatPage key={route.boatId} boatId={route.boatId} />;
    case "availability":
      return <AvailabilityPage boatId={route.boatId} />;
    case "users":
      return <UsersPage />;
    case "reserve":
      return (
        <ReservePage
          key={routeToHash(route)}
          boatId={route.boatId}
          trip={route.trip}
        />
      );
    case "invite":
    case "resetPassword":
    case "forgotPassword":
    case "guestWaiver":
    case "agreement":
      return <PublicPage route={route} />;
  }
};

/** Pages opened from emailed links or the sign-in screen; they work signed in or out. */
const PublicPage = ({ route }: { route: Route }) => {
  switch (route.page) {
    case "invite":
      // Keyed by token so opening a second link starts fresh.
      return <AcceptInvitePage key={route.token} token={route.token} />;
    case "resetPassword":
      return <ResetPasswordPage key={route.token} token={route.token} />;
    case "guestWaiver":
      return <GuestWaiverPage key={route.token} token={route.token} />;
    case "agreement":
      return <PublicAgreementPage />;
    default:
      return <ForgotPasswordPage />;
  }
};

const SignedInApp = ({ requested }: { requested: Route }) => {
  const classes = useStyles();
  const user = useCurrentUser();
  const route = canView(user.role, requested) ? requested : DEFAULT_ROUTE;

  if (user.role === "veteran" && !user.waiverSigned) {
    return <AgreementPage />;
  }
  return (
    <>
      <NavBar route={route} />
      <main className={classes.main}>
        <PageContent route={route} />
      </main>
      <SiteFooter />
    </>
  );
};

export const App = () => {
  const classes = useStyles();
  const { state, retry } = useSession();
  const route = useRoute();

  if (isPublicRoute(route)) {
    return <PublicPage route={route} />;
  }
  switch (state.status) {
    case "loading":
      return (
        <div className={classes.centered}>
          <Loading label="Starting VetBoats…" />
        </div>
      );
    case "error":
      return (
        <div className={classes.centered}>
          <LoadError message={state.message} onRetry={retry} />
        </div>
      );
    case "signedOut":
      return <LoginPage expired={state.expired} />;
    case "signedIn":
      return <SignedInApp requested={route} />;
  }
};
